import React, { useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import SDAGoBack from "./SDAGoBack";
import SDASteps from "./SDASteps";
import { SDATestModeBanner, SDATestSkipButton } from "./sdaTestMode";
import {
  PaymentMethodRow,
  AddBankModal,
} from "../../components/payment";
import { usePaymentMethods } from "../../hooks/usePaymentMethods";
import { useAuth } from "../../contexts/AuthContext";

const SDAFinancial: React.FC = () => {
  const { uuid } = useParams<{ uuid: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { user } = useAuth();

  const {
    methods,
    loading,
    error,
    successMessage,
    busyId,
    refresh,
    setDefault,
    remove,
    flash,
  } = usePaymentMethods();

  const [showAddBank, setShowAddBank] = useState(false);

  const banks = useMemo(
    () => methods.filter((m) => m.type === "us_bank_account"),
    [methods],
  );

  const hasBank = banks.length > 0;
  // Ops can flip this on the user row to let specific tenants skip the
  // bank-connection step. The step UI stays visible — they can still
  // connect a bank if they want — but the Next button isn't gated on it.
  const canProceedWithoutBank = user?.skipBankRequirement === true;

  return (
    <div>
      <SDAGoBack />

      <SDATestModeBanner />

      <SDASteps current={3} />

      <div className="rounded-xl bg-white shadow-sm">
        <div className="border-b border-gray-100 px-5 py-4">
          <h3 className="text-base font-semibold text-primary">
            {t("sda.financial.title")}
          </h3>
          <p className="mt-0.5 text-sm text-muted">
            {hasBank
              ? t("sda.financial.subWithBanks")
              : t("sda.financial.subWithoutBanks")}
          </p>
        </div>

        <div className="p-6">
          {successMessage && (
            <div className="mb-4 rounded-lg bg-green-50 px-4 py-3 text-sm text-success">
              {successMessage}
            </div>
          )}
          {error && (
            <div className="mb-4 rounded-lg bg-red-50 px-4 py-3 text-sm text-danger">
              {error}
            </div>
          )}

          {loading ? (
            <p className="text-sm text-muted">{t("sda.financial.loading")}</p>
          ) : hasBank ? (
            <>
              <div className="mb-4 grid grid-cols-1 gap-4 md:grid-cols-2">
                {banks.map((m) => (
                  <PaymentMethodRow
                    key={m.id}
                    method={m}
                    onSetDefault={setDefault}
                    onRemove={remove}
                    busy={busyId === m.id}
                  />
                ))}
              </div>
              <div className="flex justify-end">
                <button
                  type="button"
                  className="rounded-lg border border-primary px-4 py-2 text-sm font-medium text-primary transition-colors hover:bg-primary/5 cursor-pointer"
                  onClick={() => setShowAddBank(true)}
                >
                  {t("sda.financial.connectAnother")}
                </button>
              </div>
            </>
          ) : (
            <div className="text-center py-2">
              <img
                alt="Plaid"
                src="/assets/plaid.svg"
                className="mx-auto mb-6 h-7"
              />
              <h4 className="mb-2 text-lg font-semibold text-primary">
                {t("sda.financial.introHeading")}
              </h4>
              <p className="mx-auto mb-2 max-w-md text-sm text-gray-600">
                {t("sda.financial.introBody1")}
              </p>
              <p className="mx-auto mb-6 max-w-md text-sm text-muted">
                {t("sda.financial.introBody2")}
              </p>
              <button
                type="button"
                className="rounded-lg bg-accent px-6 py-2.5 text-sm font-semibold text-black transition-colors hover:bg-accent-hover cursor-pointer"
                onClick={() => setShowAddBank(true)}
              >
                {t("sda.financial.connectButton")}
              </button>
            </div>
          )}
        </div>

        <div className="flex items-center justify-end gap-2 border-t border-gray-100 px-5 py-4">
          <SDATestSkipButton
            onSkip={() => navigate(`/sda/${uuid}/underwriting`)}
          />
          <button
            className="rounded-lg bg-primary px-6 py-2.5 text-sm font-medium text-white transition-colors hover:bg-primary-light disabled:cursor-not-allowed disabled:opacity-50 cursor-pointer"
            disabled={!hasBank && !canProceedWithoutBank}
            onClick={() => navigate(`/sda/${uuid}/underwriting`)}
          >
            {t("sda.financial.next")}
          </button>
        </div>
      </div>

      <AddBankModal
        open={showAddBank}
        onClose={() => setShowAddBank(false)}
        onConnected={() => {
          setShowAddBank(false);
          flash(t("sda.financial.connected"));
          refresh();
        }}
      />
    </div>
  );
};

export default SDAFinancial;
